import React, { useState } from 'react';
import { User } from 'types';
import styles from './EditUserSettings.module.scss';
import { Button } from '../../../../../../components/ui/button';
import { usePermissionCheck } from '../../../../../../core/hooks/usePermissionCheck';
import { passwordRecoveryService } from '../../../../../../core/API/passwordRecovery';
// employee notes are handled by a separate component
import { useFormik } from 'formik';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import {
	Box,
	Checkbox,
	FormControlLabel,
	Grid,
	Dialog,
	DialogActions,
	CircularProgress,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormHelperText,
} from '@mui/material';
// icons used by EmployeeNoteEdit
import InputMask from 'react-input-mask';
import BasicDatePicker from '../../../../../../core/components/DatePicker';
import dayjs from 'dayjs';
import Autocomplete from '@mui/material/Autocomplete';
import { COUNTRY_LIST, COUNTRY_STATES } from '../../constants';
import * as Yup from 'yup';
import settingsUtils from '../../utils';
import { userSettingsService } from 'core/API/userSettings';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import useUserData from '../../../../../../store/useUserData';
import EmployeeNotesScreen from '../EmployeeNotes/EmployeeNotesScreen';
import EmployeeRequestModal from '../../../../../../module/schedule/components/EmployeeRequestModal/EmployeeRequestModal';

// using settingsUtils.isValidPostalCode with country parameter

export interface WorkingHours {
	day: {
		id: number;
		label: string;
		locale: string;
		position: string;
	};
	day_end: {
		id: number;
		label: string;
		locale: string;
		position: string;
	};
	open_at: string;
	close_at: string;
	closed: boolean;
}

interface EditUserSettingProps {
	user: User;
	handleBackClick?: () => void;
}

const EditUserSettings: React.FC<EditUserSettingProps> = ({ user, handleBackClick }) => {
	const { hasExecutePermission } = usePermissionCheck();
	const canExecuteUser = hasExecutePermission('User');

	// Clean hierarchy name: remove parenthetical content and standalone 'node'
	const cleanHierarchyName = (raw?: string) => {
		if (!raw) return '';
		let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ');
		s = s.replace(/\bnode\b/gi, ' ');
		return s.replace(/\s+/g, ' ').trim();
	};
	const [currentView, setCurrentView] = useState<'edit' | 'availability-request' | 'employee-notes'>('edit');
	const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
	const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
	const [passwordResetSuccess, setPasswordResetSuccess] = useState<string | null>(null);
	const [isResettingPassword, setIsResettingPassword] = useState(false);

	// const [user, setSingleData] = useState<any | null>(user);

	// useEffect(() => {
	// 	const fetchData = async () => {
	// 		try {
	// 			const res = await getSingleWorkplace(user.id);
	// 			setSingleData(res);
	// 		} catch (err: any) {
	// 			console.error('Failed to fetch single user:', err.message);
	// 		}
	// 	};
	// 	fetchData();
	// }, [user.id]);

	const { showToast } = useToastContext();
	const initialFormikValues = React.useMemo(
		() => ({
			firstName: user?.firstName || '',
			lastName: user?.lastName || '',
			hierarchy: user?.hierarchy?.['@id'] || '',
			active: user.userType?.active ?? user.userType?.active ?? false,
			startDate: user?.startDate || dayjs(),
			securityRole: user?.securityRole?.['@id'] || '',
			phone: user?.phone || '',
			email: user?.email || '',
			middleName: user?.middleName || '',
			maritalStatus: user?.maritalStatus ? user.maritalStatus : 'Single', // Default to 'Single' if not provided
			dateOfBirth: user?.dateOfBirth || dayjs(),
			addressLine1: user?.addressLine1 || '',
			addressLine2: user?.addressLine2 || '',
			city: user?.city || '',
			state: user?.state || '',
			postcode: user?.postcode || '',
			country: user?.country || 'USA',
			gender: user?.gender || '',
			separation: user.separation,
			separationDate: user?.separationDate ? dayjs(user.separationDate) : dayjs(),
			userMustChangePasswordOnNextLogin: user?.userMustChangePasswordOnNextLogin || false,
		}),
		[
			user?.firstName,
			user?.lastName,
			user?.hierarchy,
			user?.userType?.active,
			user?.startDate,
			user?.securityRole,
			user?.phone,
			user?.email,
			user?.middleName,
			user?.maritalStatus,
			user?.dateOfBirth,
			user?.addressLine1,
			user?.addressLine2,
			user?.city,
			user?.state,
			user?.postcode,
			user?.country,
			user?.gender,
			user?.separation,
			user?.separationDate,
			user?.userMustChangePasswordOnNextLogin,
		]
	);

	const { securityRoles, hierarchyNodes } = useUserData();

	// Helpers to parse workplace id and role workplace strings
	const parseLocalWorkplaceId = () => {
		const raw = localStorage.getItem('workplace-id');
		if (!raw) return null;
		try {
			const parsed = JSON.parse(raw);
			if (typeof parsed === 'number') return parsed;
		} catch (e) {
			// ignore
		}
		const num = Number(raw);
		return isNaN(num) ? null : num;
	};

	const getWorkplaceIdFromString = (workplace?: string | null) => {
		if (!workplace) return null;
		const s = String(workplace);
		const parts = s.split('/').filter(Boolean);
		const last = parts.length ? parts[parts.length - 1] : s;
		const num = Number(last);
		return isNaN(num) ? null : num;
	};

	// Visible security roles: hide roles with 'BYOD' in the name; always include system roles; include org-level (null workplace); include those matching current workplace
	const currentWorkplaceIdForRoles = parseLocalWorkplaceId();
	const visibleSecurityRoles = (securityRoles || []).filter((r: any) => {
		if (r?.name && String(r.name).includes('BYOD')) return false;
		if ((r as any).isSystemRole) return true;
		const roleWpId = getWorkplaceIdFromString(r.workplace as any);
		if (roleWpId === null) return true;
		if (currentWorkplaceIdForRoles === null) return false;
		return roleWpId === currentWorkplaceIdForRoles;
	});

	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required('First name is required'),
		lastName: Yup.string().required('Last name is required'),
		active: Yup.boolean(),
		startDate: Yup.date().required('Start date is required'),
		securityRole: Yup.string().required('Security role is required'),
		hierarchy: Yup.string().required('Hierarchy is required'),
		phone: Yup.string()
			.required('Phone number is required')
			.test('is-complete', 'Please enter a complete phone number', (value) => {
				if (!value) return false;
				return value.replace(/[^0-9]/g, '').length === 10;
			}),
		email: Yup.string().email('Invalid email').required('Email is required'),
		middleName: Yup.string(),
		maritalStatus: Yup.string().required('Marital status is required'),
		dateOfBirth: Yup.date()
			.required('Date of birth is required')
			.max(dayjs().subtract(10, 'year').toDate(), 'User must be at least 10 years old')
			.min(dayjs().subtract(100, 'year').toDate(), 'User cannot be more than 100 years old'),
		addressLine1: Yup.string().required('Address line 1 is required'),
		addressLine2: Yup.string(),
		city: Yup.string().required('City is required'),
		state: Yup.string().required('State is required'),
		postcode: Yup.string().test('is-valid-postal-code', 'Invalid postal code', function (value) {
			const country = (this.parent as any)?.country;
			return !value ? true : settingsUtils.isValidPostalCode(value, country);
		}),
		country: Yup.string().required('Country is required'),
		gender: Yup.string().required('Gender is required'),
	});

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: initialFormikValues,
		validationSchema,
		onSubmit: async (values, { setSubmitting: _setSubmitting }) => {
			try {
				// Prepare the payload for updating the user
				const updatePayload = {
					id: user.id,
					firstName: values.firstName,
					lastName: values.lastName,
					middleName: values.middleName,
					email: values.email ? values.email.toLowerCase() : '',
					phone: values.phone,
					gender: values.gender,
					maritalStatus: values.maritalStatus,
					dateOfBirth: values.dateOfBirth || null,
					addressLine1: values.addressLine1,
					addressLine2: values.addressLine2,
					city: values.city,
					state: values.state,
					postcode: values.postcode,
					country: values.country,
					startDate: values.startDate,
					hierarchy: String(values.hierarchy),
					securityRole: values.securityRole,
					active: values.active,
					separation: values.separation,
					// When separation is false, ensure separationDate is null
					// When separation is true, use the selected date or null if not selected
					separationDate: values.separation
						? values.separationDate
							? dayjs(values.separationDate)
							: null
						: null,
					userMustChangePasswordOnNextLogin: values.userMustChangePasswordOnNextLogin,
				};

				// Convert separationDate to ISO string (UTC with milliseconds) for the API
				const apiPayload = {
					...updatePayload,
					dateOfBirth: updatePayload.dateOfBirth
						? dayjs(updatePayload.dateOfBirth).toISOString()
						: null,
					separationDate: updatePayload.separationDate
						? dayjs(updatePayload.separationDate).toISOString()
						: null,
				};

				// Call the API to update the user (cast to any because API expects ISO string but internal types use Dayjs)
				const updatedUser = await userSettingsService.updateUser(apiPayload as any);
				console.log('User updated successfully:', updatedUser);

				showToast('success', 'User updated successfully!');

				// Navigate back after a short delay
				setTimeout(() => {
					handleBackClick?.();
				}, 1500);
			} catch (err: any) {
				showToast('error', err?.message || 'Failed to update user');
			}
		},
	});

	const getStateOptions = () => {
		if (!formik.values.country) return [];
		return COUNTRY_STATES[formik.values.country] || [];
	};

	// const handleWorkingHoursChange = (value: any) => {
	// 	console.log('Working hours changed:', value);
	// 	formik.setFieldValue('working_hours', value);
	// 	setOpenWorkingHoursDialog(false);
	// };

	// note management moved to EmployeeNoteEdit component

	// Handler functions
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		formik.handleSubmit();
	};

	const handleOpenAvailabilityRequest = () => {
		setCurrentView('availability-request');
	};

	const handleOpenEmployeeNotes = () => {
		setCurrentView('employee-notes');
	};

	const handleBackToEdit = () => {
		setCurrentView('edit');
	};

	if (!user) {
		return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
	}

	// Handle availability request view
	if (currentView === 'availability-request') {
		return (
			<EmployeeRequestModal
				closeModal={handleBackToEdit}
				refetchSchedules={() => {
					// Optional: refetch user data if needed
				}}
				defaultRequestType="availability"
				hideRequestTypeSelector={true}
			/>
		);
	}

	// Handle employee notes view
	if (currentView === 'employee-notes') {
		return <EmployeeNotesScreen user={user} onBack={handleBackToEdit} />;
	}

	console.log('formik values', formik.errors);

	return (
		<div className={styles.editUserSetting}>
			<Grid container direction="column" spacing={2} pl={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid>
						<Button className={styles.backButton} onClick={handleBackClick}>
							Back
						</Button>
					</Grid>
					<Grid item xs={9} display={'flex'} justifyContent="flex-end">
						<Grid>
						<Button
								className={styles.backButton}
								onClick={() => {
									setPasswordResetDialogOpen(true);
								}}
								style={{
									marginRight: '16px',
									opacity: canExecuteUser ? 1 : 0.5,
									cursor: canExecuteUser ? 'pointer' : 'not-allowed',
								}}
								disabled={!canExecuteUser}
								title={
									canExecuteUser ? 'Reset Password' : 'You do not have permission for this action'
								}
							>
								Reset Password
							</Button>
						</Grid>
						<Grid>
							<Button
								className={styles.backButton}
								onClick={handleOpenAvailabilityRequest}
								style={{
									marginRight: '16px',
								}}
								title="Request availability change"
							>
								Availability Request
							</Button>
						</Grid>
						<Grid>
							<Button
								className={styles.backButton}
								onClick={handleOpenEmployeeNotes}
								style={{
									marginRight: '16px',
								}}
								title="View and manage employee notes"
							>
								Employee Notes
							</Button>
						</Grid>
						<Grid>
							<Button className={styles.saveButton} onClick={handleSubmit}>
								Save
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>User Details</h2>
				</Grid>
			</Grid>

			{/* <h3 className={styles.title}>{user.name}</h3> */}
			<form className={styles.formContainer} onSubmit={formik.handleSubmit}>
				{/* User Name (Email) */}
				<TextField
					label="User Name (Email)"
					name="email"
					type="email"
					value={formik.values.email || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					disabled
					fullWidth
					required
					error={formik.touched.email && Boolean(formik.errors.email)}
					helperText={formik.touched.email && formik.errors.email}
				/>

				{/* First Name (Required) */}
				<TextField
					label="First Name"
					name="firstName"
					value={formik.values.firstName}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.firstName && Boolean(formik.errors.firstName)}
					helperText={formik.touched.firstName && formik.errors.firstName}
				/>

				{/* Middle Name (Optional) */}
				<TextField
					label="Middle Name"
					name="middleName"
					value={formik.values.middleName || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* Last Name (Required) */}
				<TextField
					label="Last Name"
					name="lastName"
					value={formik.values.lastName}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					required
					error={formik.touched.lastName && Boolean(formik.errors.lastName)}
					helperText={formik.touched.lastName && formik.errors.lastName}
				/>

				<BasicDatePicker
					label="Date of Birth"
					name="dateOfBirth"
					value={formik.values.dateOfBirth ? dayjs(formik.values.dateOfBirth) : null}
					onChange={(date) => formik.setFieldValue('dateOfBirth', date)}
					required
					error={formik.touched.dateOfBirth && Boolean(formik.errors.dateOfBirth)}
					helperText={formik.touched.dateOfBirth && (formik.errors.dateOfBirth as string)}
					margin="normal"
				/>

				<BasicDatePicker
					label="Start Date"
					value={formik.values.startDate || null}
					onChange={(date) => formik.setFieldValue('startDate', date)}
					required
					error={formik.touched.startDate && Boolean(formik.errors.startDate)}
					helperText={formik.touched.startDate && (formik.errors.startDate as string)}
				/>

				{/* Cell Phone */}
				<InputMask
					mask="(999) 999 - 9999"
					value={formik.values.phone || ''}
					onChange={formik.handleChange}
					maskChar="_"
				>
					{(inputProps: any) => (
						<TextField
							{...inputProps}
							label="Cell Phone"
							name="phone"
							className={styles.input}
							margin="normal"
							fullWidth
							required
							error={formik.touched.phone && Boolean(formik.errors.phone)}
							helperText={formik.touched.phone && formik.errors.phone}
						/>
					)}
				</InputMask>

				{/* Security Role (Required, Autocomplete) */}
				<Autocomplete
					options={[...visibleSecurityRoles].sort((a, b) => a.name.localeCompare(b.name))}
					getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
					value={securityRoles.find((role) => role['@id'] === formik.values.securityRole) || null}
					onChange={(_, value) => formik.setFieldValue('securityRole', value ? value['@id'] : '')}
					isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Security Role"
							name="securityRole"
							required
							margin="normal"
							className={styles.input}
							error={formik.touched.securityRole && Boolean(formik.errors.securityRole)}
							helperText={formik.touched.securityRole && formik.errors.securityRole}
							fullWidth
						/>
					)}
				/>

				{/* Hierarchy */}
				<Autocomplete
					options={[...hierarchyNodes].sort((a, b) => {
						// First, sort by type priority: organization > custom > workplace
						const getTypePriority = (type: string) => {
							if (type === 'organization') return 0;
							if (type === 'group') return 1;
							if (type === 'workplace') return 2;
							return 3;
						};

						const aPriority = getTypePriority(a.type);
						const bPriority = getTypePriority(b.type);

						if (aPriority !== bPriority) {
							return aPriority - bPriority;
						}

						// If types are the same, sort alphabetically for custom nodes
						if (a.type === 'group' && b.type === 'group') {
							return cleanHierarchyName(a.name).localeCompare(
								cleanHierarchyName(b.name),
								undefined,
								{ sensitivity: 'base' }
							);
						}

						// For workplaces, sort by ID-Name
						if (a.type === 'workplace' && b.type === 'workplace') {
							const aId = a.id || 0;
							const bId = b.id || 0;
							if (aId !== bId) {
								return aId - bId;
							}
							return cleanHierarchyName(a.name).localeCompare(
								cleanHierarchyName(b.name),
								undefined,
								{ sensitivity: 'base' }
							);
						}

						// Default alphabetical sort (cleaned)
						return cleanHierarchyName(a.name).localeCompare(cleanHierarchyName(b.name), undefined, {
							sensitivity: 'base',
						});
					})}
					getOptionLabel={(option) => {
						if (typeof option === 'string') return option;
						if (option.type === 'workplace') {
							const id = String(option.id).padStart(3, '0');
							return `${id} - ${cleanHierarchyName(option.name)}`;
						}
						return cleanHierarchyName(option.name);
					}}
					value={hierarchyNodes.find((node) => node?.['@id'] === formik.values.hierarchy) || null}
					onChange={(_, value) => formik.setFieldValue('hierarchy', value ? value?.['@id'] : '')}
					isOptionEqualToValue={(option, value) => option?.['@id'] === value?.['@id']}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Hierarchy"
							name="hierarchy"
							required
							margin="normal"
							className={styles.input}
							error={formik.touched.hierarchy && Boolean(formik.errors.hierarchy)}
							helperText={formik.touched.hierarchy && formik.errors.hierarchy}
							fullWidth
						/>
					)}
				/>

				{/* Marital Status (Required, default Single) */}
				<FormControl fullWidth margin="normal" required className={styles.input}>
					<InputLabel id="marital-status-label">Marital Status</InputLabel>
					<Select
						labelId="marital-status-label"
						id="maritalStatus"
						name="maritalStatus"
						value={formik.values.maritalStatus || 'Single'}
						label="Marital Status"
						onChange={formik.handleChange}
					>
						<MenuItem value="Single">Single</MenuItem>
						<MenuItem value="Married">Married</MenuItem>
					</Select>
				</FormControl>

				{/* Gender (Required) */}
				<FormControl
					fullWidth
					margin="normal"
					required
					className={styles.input}
					error={formik.touched.gender && Boolean(formik.errors.gender)}
				>
					<InputLabel id="gender-label">Gender</InputLabel>
					<Select
						labelId="gender-label"
						id="gender"
						name="gender"
						value={formik.values.gender || ''}
						label="Gender"
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
					>
						<MenuItem value="Male">Male</MenuItem>
						<MenuItem value="Female">Female</MenuItem>
						<MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
					</Select>
					{formik.touched.gender && formik.errors.gender && (
						<FormHelperText>{formik.errors.gender}</FormHelperText>
					)}
				</FormControl>

				{/* Address Fields (Required) */}
				<TextField
					label="Address Line 1"
					name="addressLine1"
					required
					value={formik.values.addressLine1 || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="Address Line 2"
					name="addressLine2"
					value={formik.values.addressLine2 || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>
				<TextField
					label="City"
					name="city"
					value={formik.values.city || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
				/>

				{/* State */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={getStateOptions()}
						getOptionLabel={(option) => option}
						value={formik.values.state || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('state', newValue || '');
						}}
						disabled={!formik.values.country}
						renderInput={(params) => (
							<TextField
								{...params}
								label="State"
								error={formik.touched.state && Boolean(formik.errors.state)}
								helperText={formik.touched.state && (formik.errors.state as string)}
								required
							/>
						)}
					/>
				</FormControl>
				<TextField
					label="Postal Code"
					name="postcode"
					value={formik.values.postcode || ''}
					onChange={formik.handleChange}
					className={styles.input}
					margin="normal"
					fullWidth
					error={formik.touched.postcode && Boolean(formik.errors.postcode)}
					helperText={formik.touched.postcode && formik.errors.postcode}
				/>

				{/* Country */}
				<FormControl fullWidth margin="normal">
					<Autocomplete
						options={COUNTRY_LIST}
						getOptionLabel={(option) => option}
						value={formik.values.country || null}
						onChange={(_, newValue) => {
							formik.setFieldValue('country', newValue || '');
							formik.setFieldValue('state', ''); // Reset state when country changes
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label="Country"
								error={formik.touched.country && Boolean(formik.errors.country)}
								helperText={formik.touched.country && (formik.errors.country as string)}
								required
							/>
						)}
					/>
				</FormControl>

				<Box>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.userMustChangePasswordOnNextLogin}
								onChange={() => {
									formik.setFieldValue(
										'userMustChangePasswordOnNextLogin',
										!formik.values.userMustChangePasswordOnNextLogin
									);
								}}
							/>
						}
						label={'User must change password on next login'}
					/>
				</Box>

				<Box>
					<FormControlLabel
						control={
							<Checkbox
								checked={formik.values.separation}
								onChange={() => {
									formik.setFieldValue('separation', !formik.values.separation);
								}}
							/>
						}
						label={'Separation'}
					/>
				</Box>
				{formik.values.separation && (
					<BasicDatePicker
						label="Separation Date"
						value={formik.values.separationDate || null}
						onChange={(date) => formik.setFieldValue('separationDate', date)}
						required
						error={formik.touched.separationDate && Boolean(formik.errors.separationDate)}
						helperText={formik.touched.separationDate && (formik.errors.separationDate as string)}
					/>
				)}
			</form>

			{/* Password Reset Confirmation Dialog */}
			<Dialog
				open={passwordResetDialogOpen}
				onClose={() => {
					setPasswordResetDialogOpen(false);
					setPasswordResetError(null);
					setPasswordResetSuccess(null);
				}}
				aria-labelledby="password-reset-dialog-title"
				aria-describedby="password-reset-dialog-description"
			>
				<DialogTitle id="password-reset-dialog-title">Confirm Password Reset</DialogTitle>
				<DialogContent>
					<DialogContentText id="password-reset-dialog-description">
						Are you sure you want to reset the password for {user.firstName} {user.lastName}? An
						email will be sent to {user.email} with instructions to reset the password.
					</DialogContentText>
					{passwordResetError && (
						<DialogContentText color="error">{passwordResetError}</DialogContentText>
					)}
					{passwordResetSuccess && (
						<DialogContentText color="success.main">{passwordResetSuccess}</DialogContentText>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						variant="outline"
						className={styles.dialogBackButton}
						onClick={() => {
							setPasswordResetDialogOpen(false);
							setPasswordResetError(null);
							setPasswordResetSuccess(null);
							setIsResettingPassword(false);
						}}
						disabled={isResettingPassword}
					>
						Cancel
					</Button>
					{!passwordResetSuccess && (
						<Button
							className={styles.dialogBackButton}
							onClick={async () => {
								if (isResettingPassword) return;
								try {
									setIsResettingPassword(true);
									setPasswordResetError(null);
									setPasswordResetSuccess(null);

									const tenant = localStorage.getItem('BYOD-organizationId');
									if (!tenant) {
										throw new Error('Organization ID not found');
									}

									await passwordRecoveryService.resetPassword(user.email, tenant);

									// Close dialog immediately and show success toast
									setPasswordResetDialogOpen(false);
									setPasswordResetSuccess(null);
									setIsResettingPassword(false);
									showToast('success', 'Password reset email has been sent successfully.');
								} catch (error: any) {
									// Close dialog immediately and show error toast
									setPasswordResetDialogOpen(false);
									setPasswordResetError(null);
									setIsResettingPassword(false);
									showToast('error', error.message || 'Failed to reset password');
								}
							}}
							disabled={isResettingPassword}
						>
							{isResettingPassword ? 'Sending...' : 'Confirm'}
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default EditUserSettings;
